import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultLanguage, SupportedLanguages } from './config';
import type { SupportedLanguage } from './config';

interface UseUserLanguageResult {
  /**
   * The active language code. Resolution priority:
   *   1. `User.PreferredLanguage` — once IDN ships and populates the user
   *      context (a future block will read it from `useCurrentUser()` here).
   *   2. Browser language detector (`localStorage > navigator > htmlTag`),
   *      narrowed to a `SupportedLanguage`.
   *   3. `DefaultLanguage` (`en-US`) when none of the above resolve.
   */
  language: SupportedLanguage;

  /**
   * IANA time zone for date formatting. Today this is browser-detected;
   * once IDN ships it will prefer `User.PreferredTimeZone` per
   * [architecture §5.9.3](../../../../docs/shared/architecture.md). Module
   * work that calls `formatDate(...)` must read this hook instead of calling
   * `Intl.DateTimeFormat().resolvedOptions().timeZone` directly so the
   * IDN-driven override applies uniformly.
   */
  timeZone: string;

  /**
   * Switches the active language. Persisted to `localStorage` by the i18next
   * language detector so the choice survives reloads.
   */
  setLanguage: (next: SupportedLanguage) => Promise<void>;
}

function narrowLanguage(value: string | undefined): SupportedLanguage {
  if (!value) {
    return DefaultLanguage;
  }
  const exact = SupportedLanguages.find((lng) => lng === value);
  if (exact) {
    return exact;
  }
  const base = value.split('-')[0];
  const baseMatch = SupportedLanguages.find((lng) => lng.split('-')[0] === base);
  return baseMatch ?? DefaultLanguage;
}

function resolveTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Single source of truth for the SPA's active language and time-zone.
 * Module components should call `useUserLanguage()` rather than
 * `useTranslation().i18n.language` directly so the IDN-backed values flow
 * through one place.
 */
export function useUserLanguage(): UseUserLanguageResult {
  const { i18n } = useTranslation();

  const language = useMemo(() => narrowLanguage(i18n.resolvedLanguage), [i18n.resolvedLanguage]);
  const timeZone = useMemo(() => resolveTimeZone(), []);

  useEffect(() => {
    if (i18n.resolvedLanguage !== language) {
      void i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  const setLanguage = useCallback(
    async (next: SupportedLanguage) => {
      await i18n.changeLanguage(next);
    },
    [i18n],
  );

  return { language, timeZone, setLanguage };
}
